import { useParams, Link } from 'react-router-dom';
import { Helmet } from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getBreadcrumbJsonLD } from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const comic = comics.find((c) => c.id === articleId);

  if (!comic) {
    return (
      <main className="min-h-screen bg-background-50 pt-20">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-foreground-600 mb-6">您访问的漫画不存在或已下架</p>
          <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
            返回首页
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${comic.category}漫画`, href: `/category/${comic.categoryId}` },
    { label: comic.title },
  ];

  const breadcrumbSchema = getBreadcrumbJsonLD(
    breadcrumbItems,
    'https://www.hanmanzhijia.com'
  );

  const relatedComics = comics
    .filter((c) => c.categoryId === comic.categoryId && c.id !== comic.id)
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${comic.title} - 韩漫之家`,
        description: comic.description,
        author: {
          '@type': 'Person',
          name: comic.author,
        },
        datePublished: comic.updateDate,
        dateModified: comic.updateDate,
        image: comic.cover,
        publisher: {
          '@type': 'Organization',
          name: '韩漫之家',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://www.hanmanzhijia.com/article/${comic.id}`,
        },
      },
      breadcrumbSchema,
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `${comic.title}是免费的吗？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `是的，${comic.title}在韩漫之家完全免费阅读，无需付费或会员。`,
            },
          },
          {
            '@type': 'Question',
            name: `${comic.title}多久更新一次？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `目前${comic.title}处于${comic.status}状态，最新章节为第${comic.chapters.length}话，请关注韩漫之家获取最新更新通知。`,
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet
        title={`${comic.title}_免费在线阅读-韩漫之家`}
        description={`${comic.title} - ${comic.description.slice(0, 120)}。韩漫之家提供免费高清${comic.title}在线阅读，${comic.status}，共${comic.chapters.length}话。`}
        keywords={`${comic.title},${comic.title}韩漫,${comic.title}免费,${comic.category}漫画`}
        canonicalUrl={`https://www.hanmanzhijia.com/article/${comic.id}`}
        ogTitle={`${comic.title} - 韩漫之家免费在线阅读`}
        ogDescription={`${comic.title} - ${comic.description.slice(0, 120)}`}
        ogImage={comic.cover}
        schema={articleSchema}
      />
      <main className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-20 pb-8 md:pb-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-6 flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-full sm:w-64 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩国漫画在线阅读`}
                    title={`${comic.title} - 免费高清韩漫`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-500">作者</p>
                    <p className="text-sm font-medium text-foreground-950 mt-0.5">{comic.author}</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-500">状态</p>
                    <p className="text-sm font-medium text-foreground-950 mt-0.5">
                      <span className={comic.status === '连载中' ? 'text-accent-600' : 'text-foreground-600'}>
                        {comic.status}
                      </span>
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-500">评分</p>
                    <p className="text-sm font-medium text-accent-600 mt-0.5">
                      <span className="inline-flex items-center gap-0.5">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-xs"></i>
                        </div>
                        {comic.rating}
                      </span>
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-500">阅读量</p>
                    <p className="text-sm font-medium text-foreground-950 mt-0.5">
                      {(comic.views / 10000).toFixed(1)}万
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="text-base font-heading font-bold text-foreground-950 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex items-center gap-3">
                  <Link
                    to={`/article/${comic.id}`}
                    className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer" aria-label="收藏">
                    <i className="ri-bookmark-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer" aria-label="分享">
                    <i className="ri-share-line"></i>
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                <h2 className="text-lg font-heading font-bold text-foreground-950">
                  章节列表
                  <span className="text-sm font-normal text-foreground-500 ml-2 font-body">共{comic.chapters.length}话</span>
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {comic.chapters.map((ch) => (
                  <a
                    key={ch.id}
                    href={`#chapter-${ch.number}`}
                    className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-background-100 hover:bg-primary-50 hover:text-primary-500 text-sm text-foreground-700 transition-colors cursor-pointer"
                  >
                    <span className="whitespace-nowrap font-medium">{ch.title}</span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.updateDate}</span>
                  </a>
                ))}
              </div>
            </section>

            {relatedComics.length > 0 && (
              <section className="mt-12">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-5 rounded-full bg-accent-500"></div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950">
                    相关推荐
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} variant="default" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        <Footer />
        <ScrollToTop />
      </main>
    </>
  );
}